import React, { useEffect, useRef, useState } from 'react';
import { rollDice, rollExplodingDice, ExplodeResult } from '../../utils/roll';
import './styles.css';
import { DiceResult } from '../DiceResult';
import { LAST_ROLL_KEY } from '../../consts/consts';
import { publishRoll } from '../../utils/roomClient';
import { DiceConfig, DICE_SIDES, DICE_TYPES, DiceType, RoomMode } from '../../types/RoomMode';
import { RollGroup } from '../../utils/roomClient/types';

const MAX_INITIAL_DICE = 20;
const MIN_DIFFICULTY = 4;
const MAX_DIFFICULTY = 6;

const EMPTY_DICE_COUNTS: Record<DiceType, number> = {
    d4: 0, d6: 0, d8: 0, d10: 0, d12: 0, d20: 0, d100: 0, df: 0,
};

interface Props {
    onRoll?: (payload: { chains: number[][]; difficulty: number }) => void;
    roomId?: string | null;
    playerId: string;
    showInlineResult?: boolean;
    roomMode?: RoomMode;
    diceConfig?: DiceConfig;
}

export type StoredLastRoll = {
    payload: {
        chains: number[][];
        difficulty: number;
        diceType?: DiceType;
        rollGroups?: RollGroup[];
    };
    timestamp: number;
    roomId?: string | null;
};

export const loadLastRollLocal = (): StoredLastRoll | null => {
    try {
        const raw = localStorage.getItem(LAST_ROLL_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as StoredLastRoll;
    } catch {
        return null;
    }
};
const saveLastRollLocal = (entry: StoredLastRoll | null) => {
    try {
        if (!entry) localStorage.removeItem(LAST_ROLL_KEY);
        else localStorage.setItem(LAST_ROLL_KEY, JSON.stringify(entry));
    } catch { /* ignore */ }
};

function DiceIcon({ type, active }: { type: DiceType; active: boolean }) {
    const shared = {
        fill: 'none' as const,
        stroke: 'currentColor',
        strokeWidth: 7,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };
    return (
        <svg viewBox="0 0 100 100" className={`dice-icon${active ? ' dice-icon--active' : ''}`} aria-hidden="true">
            {type === 'd4'   && <polygon points="50,8 94,92 6,92" {...shared} />}
            {type === 'd6'   && <rect x="12" y="12" width="76" height="76" rx="8" {...shared} />}
            {type === 'd8'   && <polygon points="50,6 94,50 50,94 6,50" {...shared} />}
            {type === 'd10'  && <polygon points="50,6 90,42 72,92 28,92 10,42" {...shared} />}
            {type === 'd12'  && <polygon points="50,6 93,36 77,90 23,90 7,36" {...shared} />}
            {type === 'd20'  && <polygon points="50,6 88,28 88,72 50,94 12,72 12,28" {...shared} />}
            {type === 'd100' && <polygon points="50,7 84,23 92,60 69,89 31,89 8,60 16,23" {...shared} />}
            {type === 'df'   && <>
                <rect x="12" y="12" width="76" height="76" rx="8" {...shared} />
                <text x="50" y="50" textAnchor="middle" dominantBaseline="middle"
                    fill="currentColor" stroke="none" fontSize="44" fontWeight="700">+</text>
            </>}
        </svg>
    );
}

const DiceRoller: React.FC<Props> = ({ roomId, playerId, showInlineResult = true, roomMode = 'brio', diceConfig }) => {
    const [count, setCount] = useState<number>(1);
    const [difficulty, setDifficulty] = useState<number>(MIN_DIFFICULTY);
    const [diceCounts, setDiceCounts] = useState<Record<DiceType, number>>({ ...EMPTY_DICE_COUNTS });
    const [result, setResult] = useState<ExplodeResult | null>(null);
    const [lastDiceType, setLastDiceType] = useState<DiceType | null>(null);
    const [lastRollGroups, setLastRollGroups] = useState<RollGroup[] | null>(null);

    const isOnlyDice = roomMode === 'only-dice';

    const publishResult = async (res: ExplodeResult, payload: { difficulty?: number; diceType?: DiceType; rollGroups?: RollGroup[] }) => {
        const entry: StoredLastRoll = {
            payload: {
                chains: res.chains,
                difficulty: payload.difficulty ?? difficulty,
                ...(payload.diceType ? { diceType: payload.diceType } : {}),
                ...(payload.rollGroups?.length ? { rollGroups: payload.rollGroups } : {}),
            },
            timestamp: Date.now(),
            roomId: roomId ?? null,
        };
        saveLastRollLocal(entry);

        if (roomId) {
            try {
                await publishRoll(roomId, playerId, {
                    chains: res.chains,
                    difficulty: payload.difficulty,
                    diceType: payload.diceType,
                    rollGroups: payload.rollGroups,
                });
            } catch (err) {
                console.error('Failed to publish roll', err);
            }
        }
    };

    const audioRef = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        try {
            audioRef.current = new Audio('/dice.mp3');
            audioRef.current.load();
        } catch {
            audioRef.current = null;
        }
        return () => { audioRef.current = null; };
    }, []);

    const playSound = () => {
        try {
            if (audioRef.current) {
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(() => { /* ignore */ });
            }
        } catch { /* ignore */ }
    };

    const handleRollBrio = () => {
        const res = rollExplodingDice(count, MAX_INITIAL_DICE);
        setResult(res);
        setLastDiceType(null);
        setLastRollGroups(null);
        playSound();
        void publishResult(res, { difficulty });
    };

    const handleRollOnlyDice = () => {
        const allChains: number[][] = [];
        const rollGroups: RollGroup[] = [];
        const exploding = diceConfig?.exploding ?? false;

        DICE_TYPES.forEach(dt => {
            const n = diceCounts[dt];
            if (n <= 0) return;
            const res = rollDice(n, DICE_SIDES[dt], exploding && dt !== 'df', MAX_INITIAL_DICE);
            allChains.push(...res.chains);
            rollGroups.push({ diceType: dt, chains: res.chains });
        });

        if (allChains.length === 0) return;

        const combined: ExplodeResult = { chains: allChains, timestamp: Date.now() };
        const primaryType = rollGroups.length === 1 ? rollGroups[0].diceType : null;

        setResult(combined);
        setLastDiceType(primaryType);
        setLastRollGroups(rollGroups);
        playSound();
        void publishResult(combined, { diceType: primaryType ?? undefined, rollGroups });
    };

    const incDiceCount = (dt: DiceType) =>
        setDiceCounts(prev => ({ ...prev, [dt]: Math.min(MAX_INITIAL_DICE, prev[dt] + 1) }));
    const decDiceCount = (dt: DiceType) =>
        setDiceCounts(prev => ({ ...prev, [dt]: Math.max(0, prev[dt] - 1) }));
    const hasAnyDice = DICE_TYPES.some(dt => diceCounts[dt] > 0);

    const decCount = () => setCount(c => Math.max(1, c - 1));
    const incCount = () => setCount(c => Math.min(MAX_INITIAL_DICE, c + 1));
    const decDifficulty = () => setDifficulty(d => Math.max(MIN_DIFFICULTY, d - 1));
    const incDifficulty = () => setDifficulty(d => Math.min(MAX_DIFFICULTY, d + 1));

    if (isOnlyDice) {
        return (
            <div className="dice-roller-root">
                <div className="dice-counts-grid">
                    {DICE_TYPES.map(dt => {
                        const n = diceCounts[dt];
                        const active = n > 0;
                        return (
                            <div key={dt} className={`dice-card${active ? ' dice-card--active' : ''}`}>
                                <DiceIcon type={dt} active={active} />
                                <div className="dice-card-type">{dt.toUpperCase()}</div>
                                <div className="dice-card-count">{n}</div>
                                <div className="dice-card-stepper">
                                    <button
                                        type="button"
                                        className="dice-card-btn"
                                        onClick={() => decDiceCount(dt)}
                                        aria-label={`Reducir ${dt}`}
                                        disabled={n === 0}
                                    >−</button>
                                    <button
                                        type="button"
                                        className="dice-card-btn"
                                        onClick={() => incDiceCount(dt)}
                                        aria-label={`Aumentar ${dt}`}
                                    >+</button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="dice-actions">
                    <button
                        className="button button-full-width"
                        onClick={handleRollOnlyDice}
                        disabled={!hasAnyDice}
                    >
                        Lanzar
                    </button>
                </div>

                {showInlineResult && result ? (
                    <div className="dice-result-area">
                        <DiceResult chains={result.chains} diceType={lastDiceType ?? undefined} rollGroups={lastRollGroups ?? undefined} />
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className="dice-roller-root">
            <div className="dice-form">
                <div className="dice-item">
                    <strong className='dice-label'>Dificultad:</strong>
                    <button
                        type="button"
                        className="button button-step"
                        onClick={decDifficulty}
                        aria-label="Disminuir dificultad"
                    >−</button>
                    <div className='dice-item-value'>{difficulty}</div>
                    <button
                        type="button"
                        className="button button-step"
                        onClick={incDifficulty}
                        aria-label="Aumentar dificultad"
                    >+</button>
                </div>

                <div className="dice-item">
                    <strong className='dice-label'>Dados:</strong>
                    <button
                        type="button"
                        className="button button-step"
                        onClick={decCount}
                        aria-label="Disminuir dados"
                    >−</button>
                    <div className='dice-item-value'>{count}</div>
                    <button
                        type="button"
                        className="button button-step"
                        onClick={incCount}
                        aria-label="Aumentar dados"
                    >+</button>
                </div>

                <div className="dice-actions">
                    <button className="button button-full-width" onClick={handleRollBrio}>Lanzar</button>
                </div>
            </div>

            {showInlineResult ? (
                <div className="dice-result-area">
                    { result && <DiceResult chains={result.chains} difficulty={difficulty} /> }
                </div>
            ) : null}
        </div>
    );
};

export default DiceRoller;
